// FILE: apps/admin/src/pages/Broadcasts.tsx
// VERSION: 1.0.0
// START_MODULE_CONTRACT
//   PURPOSE: Admin Telegram Broadcasts campaign manager supporting 6 audience segments, HTML formatting preview, inline CTA button, and delivery telemetry.
//   SCOPE: Broadcasts component.
//   DEPENDS: ../api.js, ../toast.js
//   LINKS: M-ADMIN
//   ROLE: RUNTIME
//   MAP_MODE: EXPORTS
// END_MODULE_CONTRACT
//
// START_MODULE_MAP
//   Broadcasts - Admin Telegram Broadcasts campaign composer and history table
// END_MODULE_MAP

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { api, dt, num } from '../api.js';
import { AdminIcon } from '../components/Icons.js';
import { useToast } from '../toast.js';

interface BroadcastItem {
  id: string;
  title: string;
  audienceSegment: string;
  messageHtml: string;
  buttonLabel: string | null;
  buttonUrl: string | null;
  state: string;
  totalRecipients: number;
  sentCount: number;
  failedCount: number;
  createdAt: string;
}

const SEGMENTS = [
  { id: 'all', label: 'Все игроки (All)' },
  { id: 'buyers', label: 'Покупатели Stars/Premium' },
  { id: 'non_buyers', label: 'Без покупок (Non-buyers)' },
  { id: 'dead_fish', label: 'Рыбка погибла (Реактивация)' },
  { id: 'vip_lvl5_plus', label: 'VIP Игроки (Уровень 5+)' },
  { id: 'inactive_7d', label: 'Не заходили > 7 дней' },
] as const;

// START_CONTRACT: Broadcasts
//   PURPOSE: Render the Broadcast campaign composer and delivery history table.
//   INPUTS: none
//   OUTPUTS: React element
//   SIDE_EFFECTS: Calls GET /admin/broadcasts and POST /admin/broadcasts
//   LINKS: M-ADMIN
// END_CONTRACT: Broadcasts
export function Broadcasts() {
  // START_BLOCK_BROADCASTS
  const qc = useQueryClient();
  const toast = useToast();

  const [title, setTitle] = useState('');
  const [segment, setSegment] = useState('all');
  const [messageHtml, setMessageHtml] = useState('');
  const [buttonText, setButtonText] = useState('');
  const [buttonUrl, setButtonUrl] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-broadcasts'],
    queryFn: () => api.get<{ items: BroadcastItem[] }>('/admin/broadcasts'),
    refetchInterval: 10000,
  });

  const sendMut = useMutation({
    mutationFn: () =>
      api.post('/admin/broadcasts', {
        title: title.trim(),
        segment,
        messageHtml: messageHtml.trim(),
        buttonText: buttonText.trim() || null,
        buttonUrl: buttonUrl.trim() || null,
        reason: `Запуск рассылки "${title.trim()}" по сегменту ${segment}`,
      }),
    onSuccess: () => {
      toast('Кампания рассылки запущена');
      setTitle('');
      setMessageHtml('');
      void qc.invalidateQueries({ queryKey: ['admin-broadcasts'] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Ошибка запуска рассылки'),
  });

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Рассылки в Telegram-боте</h1>
          <div className="page-subtitle">
            Таргетированные рассылки по сегментам игроков с поддержкой HTML-разметки и inline-кнопок
          </div>
        </div>
      </div>

      <div className="grid cols-2" style={{ marginBottom: 22, alignItems: 'start' }}>
        <div className="card">
          <h2 style={{ marginTop: 0 }}>Новая рассылка</h2>
          <div className="grid cols-2" style={{ marginBottom: 10 }}>
            <div>
              <label className="muted" style={{ fontSize: 12 }}>Название кампании *</label>
              <input
                style={{ width: '100%' }}
                placeholder="Вечерний дроп Stars"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <div>
              <label className="muted" style={{ fontSize: 12 }}>Сегмент аудитории</label>
              <select
                style={{ width: '100%', minWidth: 0 }}
                value={segment}
                onChange={(e) => setSegment(e.target.value)}
              >
                {SEGMENTS.map((s) => (
                  <option key={s.id} value={s.id}>{s.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ marginBottom: 10 }}>
            <label className="muted" style={{ fontSize: 12 }}>
              Текст сообщения (поддерживаются теги &lt;b&gt;, &lt;i&gt;, &lt;code&gt;) *
            </label>
            <textarea
              rows={5}
              style={{ width: '100%' }}
              placeholder="<b>Твоя рыбка скучает!</b> Загляни в аквариум и получи бонусные Stars…"
              value={messageHtml}
              onChange={(e) => setMessageHtml(e.target.value)}
            />
          </div>

          <div className="grid cols-2" style={{ marginBottom: 14 }}>
            <div>
              <label className="muted" style={{ fontSize: 12 }}>Текст кнопки (опционально)</label>
              <input
                style={{ width: '100%' }}
                placeholder="Открыть Stars Fish"
                value={buttonText}
                onChange={(e) => setButtonText(e.target.value)}
              />
            </div>
            <div>
              <label className="muted" style={{ fontSize: 12 }}>URL кнопки</label>
              <input
                style={{ width: '100%' }}
                placeholder="https://t.me/..."
                value={buttonUrl}
                onChange={(e) => setButtonUrl(e.target.value)}
              />
            </div>
          </div>

          <div className="row" style={{ justifyContent: 'flex-end' }}>
            <button
              type="button"
              className="btn primary"
              disabled={!title.trim() || !messageHtml.trim() || sendMut.isPending}
              onClick={() => sendMut.mutate()}
            >
              <AdminIcon name="send" size={13} />
              <span>{sendMut.isPending ? 'Отправка…' : 'Запустить рассылку'}</span>
            </button>
          </div>
        </div>

        <div className="card">
          <h2 style={{ marginTop: 0 }}>История кампаний</h2>
          {isLoading ? (
            <div className="muted">Загрузка…</div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Кампания</th>
                    <th>Сегмент</th>
                    <th>Доставлено / Ошибки</th>
                    <th>Статус</th>
                    <th>Дата</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.items ?? []).map((b) => (
                    <tr key={b.id}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{b.title}</div>
                        <div className="muted" style={{ fontSize: 11 }}>
                          {b.messageHtml.slice(0, 60)}…
                        </div>
                      </td>
                      <td><span className="chip info">{b.audienceSegment}</span></td>
                      <td className="mono">
                        {num(b.sentCount)} / {num(b.totalRecipients)} (ош: {num(b.failedCount)})
                      </td>
                      <td>
                        <span className={`chip ${b.state}`}>{b.state}</span>
                      </td>
                      <td className="muted">{dt(b.createdAt)}</td>
                    </tr>
                  ))}
                  {!data?.items?.length && (
                    <tr>
                      <td colSpan={5} className="muted" style={{ textAlign: 'center', padding: 24 }}>
                        Рассылок ещё не было
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
  // END_BLOCK_BROADCASTS
}
